import React, { useState, useEffect } from "react";
import errorToast from "helpers/errorToast";
import { useFormContext } from "react-hook-form";
import { useIntl } from "react-intl";

import Button from "components/atoms/Button/Button";
import { RequestFormState } from "pages/shipper/RequestForm/RequestFormState";
import { uploadFile, getFile } from "api";
import { getAllFiles } from "helpers/requestFormHelper";
import { ReactComponent as DeleteIcon } from "assets/images/icons/Delete.svg";
import { IFiles } from "../Types";

interface IAttachFileProps {
  state: RequestFormState;
}

function FileAttachment({ state }: IAttachFileProps) {
  const intl = useIntl();
  const { setValue, getValues } = useFormContext();

  const [allFiles, setAllFiles] = useState<IFiles[]>([]);
  const [referenceFile, setReferenceFile] = useState<string[]>([]);
  const [attachmentFiles, setAttachmentFiles] = useState<any>([]);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const hiddenFileInput = React.useRef<HTMLInputElement>(null);
  const [isLoadingFiles, setIsLoadingFiles] = useState<boolean>(false);

  const handleClick = () => {
    hiddenFileInput.current?.click();
  };

  // const onRevertToManualCustomsInformation = () => {
  //   revertToManualCustomsInformation(false);
  //   setValue(`good_customs_information`, []);
  //   setAllFiles([]);
  // };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>, inx: number) => {
    if (!event.target.files) return;

    const fileUploaded = event.target.files[0];
    uploadFiles(fileUploaded, inx);
  };

  function uploadFiles(file: any, inx: number) {
    setIsUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("filename", file.name);
    uploadFile(formData)
      .then((res) => {
        if (res.status === 201) {
          const fileNameWithoutPath = res.data.name.substring(res.data.name.lastIndexOf("/") + 1);
          const file = {
            fileName: fileNameWithoutPath,
            url: res.data.url,
            path: res.data.name,
            reference: res.data.reference_number,
            commodity: inx,
          };
          setReferenceFile([...referenceFile, file.reference]);
          setAllFiles((allFiles) => [...allFiles, file]);
          if (file.reference) {
            const attachments = {
              value: null,
              currency_code: state?.locationDetails?.default_currency,
              description: null,
              hs_code: null,
              attachment: [file.reference],
            };
            setAttachmentFiles([...attachmentFiles, attachments]);
            setValue(`good_customs_information`, [...attachmentFiles, attachments]);
          }
        }
      })
      .catch((error) => {
        errorToast(error);
      })
      .finally(() => {
        setIsUploading(false);
      });
  }

  const handleRemoveFile = (reference: string) => {
    const newAttachments = allFiles.filter((file: IFiles) => file.reference !== reference);
    setAllFiles(newAttachments);
    setAttachmentFiles(newAttachments);
    if (getValues(`good_customs_information`)?.length > 0) {
      const references = getValues(`good_customs_information`).filter((file: any) => file?.attachment[0] !== reference);
      setValue(`good_customs_information`, references);
    }
  };

  useEffect(() => {
    if (getValues(`good_customs_information`)?.length > 0) {
      setIsLoadingFiles(true);
      setAttachmentFiles(getValues(`good_customs_information`));
      getValues(`good_customs_information`).map((file: any) =>
        getFile(file?.attachment[0])
          .then((res) => {
            const fileNameWithoutPath = res.data.name.substring(res.data.name.lastIndexOf("/") + 1);
            const file = {
              fileName: fileNameWithoutPath,
              url: res.data.url,
              path: res.data.name,
              reference: res.data.reference_number,
              commodity: 0,
            };
            setAllFiles((allFiles) => [...allFiles, file]);
            if (allFiles?.length > 0) {
              setAllFiles((allFiles) => getAllFiles(allFiles));
            }
          })
          .finally(() => setIsLoadingFiles(false)),
      );
    }
  }, [getValues(`good_customs_information`)]);

  return (
    <div className="mt-2">
      {allFiles?.length > 0 ? (
        allFiles?.map((file, index) => (
          <div className="ml-2">
            <div className="d-flex mb-2">
              <a href={file.url} download>
                {`${index + 1}.`} {file.fileName}
              </a>
              <div className="d-flex ml-2 cursor-pointer align-items-end">
                <DeleteIcon width="14" onClick={() => handleRemoveFile(file.reference)} />
              </div>
            </div>
            <input
              type="file"
              ref={hiddenFileInput}
              onChange={(e) => handleChange(e, 0)}
              className="d-none"
              accept="application/pdf, image/gif, image/tiff, text/csv, application/vnd.apple.pages, application/vnd.apple.keynote, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.presentationml.presentation, application/msword, application/vnd.ms-powerpoint, application/vnd.openxmlformats-officedocument.wordprocessingml.document, image/jpeg, image/png"
            />
          </div>
        ))
      ) : (
        <input
          type="file"
          ref={hiddenFileInput}
          onChange={(e) => handleChange(e, allFiles?.length)}
          className="d-none"
          accept="application/pdf, image/gif, image/tiff, text/csv, application/vnd.apple.pages, application/vnd.apple.keynote, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.presentationml.presentation, application/msword, application/vnd.ms-powerpoint, application/vnd.openxmlformats-officedocument.wordprocessingml.document, image/jpeg, image/png"
        />
      )}
      {(isUploading || isLoadingFiles) && <div className="spinner-border spinner-border-sm ml-4 mb-2 mt-2" role="status" />}
      <Button
        variant="link"
        className="text-primary mt-lg py-0"
        label={intl.formatMessage({
          id: "tracking.components.uploadFileDialog.uploadedFile",
          defaultMessage: "Upload file",
        })}
        onClick={handleClick}
      />
    </div>
  );
}

export default FileAttachment;
